// Redirect-back helpers for auth flows.
//
// Usage:
//   Before nav("/login") or nav("/register") — call `promptLogin(nav)` or
//   `promptRegister(nav)`. These capture the CURRENT URL + scrollY into
//   sessionStorage so the auth pages know where to return the user afterwards.
//
//   Inside Login/Register/AuthCallback success paths — call
//   `redirectAfterAuth(nav)` (or `restoreAfterAuth(nav)`) which pops the
//   stored return_to and navigates back, then schedules a scroll restore
//   (retries for up to ~2s while lazy content mounts, e.g. thread replies).
//
// Storage key: k12_return_to = { path, scrollY, savedAt }

const KEY = "k12_return_to";
const MAX_AGE_MS = 30 * 60 * 1000; // 30 min — anything older is stale

// Paths we never want to return to after auth (auth screens themselves).
const BLOCKED_PREFIXES = ["/login", "/register", "/auth", "/logout"];

function _shouldSkip(path) {
  if (!path) return true;
  return BLOCKED_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
}

function _safeStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Save an arbitrary destination + scrollY as the return-to. */
export function saveReturnTo(path, scrollY = 0) {
  if (_shouldSkip(path)) return;
  const s = _safeStorage();
  if (!s) return;
  try {
    s.setItem(KEY, JSON.stringify({ path, scrollY: Math.max(0, Number(scrollY) || 0), savedAt: Date.now() }));
  } catch {}
}

/** Capture the CURRENT window location + scroll offset as the return-to. */
export function saveCurrentAsReturnTo() {
  const path = window.location.pathname + window.location.search + window.location.hash;
  saveReturnTo(path, window.scrollY || window.pageYOffset || 0);
}

/** Read + clear the stored return-to. Returns null if none / stale / blocked. */
export function popReturnTo() {
  const s = _safeStorage();
  if (!s) return null;
  let raw = null;
  try { raw = s.getItem(KEY); } catch { return null; }
  if (!raw) return null;
  try { s.removeItem(KEY); } catch {}
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.path) return null;
    if (Date.now() - (parsed.savedAt || 0) > MAX_AGE_MS) return null;
    if (_shouldSkip(parsed.path)) return null;
    return { path: parsed.path, scrollY: Number(parsed.scrollY) || 0 };
  } catch {
    return null;
  }
}

/** Schedule a scroll restore that retries while lazy content mounts.
 *  Bails out early once the page's scrollHeight actually reaches the target Y. */
function _restoreScroll(targetY) {
  if (!targetY || targetY <= 0) return;
  const start = Date.now();
  const tick = () => {
    const maxY = Math.max(0, (document.documentElement.scrollHeight || 0) - window.innerHeight);
    if (maxY >= targetY - 4) {
      window.scrollTo({ top: targetY, behavior: "auto" });
      return;
    }
    if (Date.now() - start > 2000) {
      // Give up gracefully — scroll to whatever we have
      window.scrollTo({ top: Math.min(targetY, maxY), behavior: "auto" });
      return;
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/** After a successful auth: nav back to the saved destination + restore scroll.
 *  fallback defaults to "/" so signed-in users don't get stuck on auth screens. */
export function redirectAfterAuth(navigate, fallback = "/") {
  const ret = popReturnTo();
  const target = ret?.path || fallback;
  navigate(target, { replace: true });
  // Give React Router a beat to swap routes, then start the scroll-restore loop.
  if (ret?.scrollY) {
    setTimeout(() => _restoreScroll(ret.scrollY), 30);
  }
}

/**
 * Peek the pending return-to WITHOUT consuming it. Returns true when the
 * saved intent is a transactional review-composer resume (path contains
 * `?review=1` on a `/sekolah/:id`). Used by post-auth routing to bypass
 * new-user onboarding when a review submission is mid-flight — the review
 * auto-submit + success page take priority; onboarding will surface on the
 * next normal entry.
 */
export function hasPendingReviewReturn() {
  const s = _safeStorage();
  if (!s) return false;
  let raw = null;
  try { raw = s.getItem(KEY); } catch { return false; }
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.path) return false;
    if (Date.now() - (parsed.savedAt || 0) > MAX_AGE_MS) return false;
    if (_shouldSkip(parsed.path)) return false;
    return /^\/sekolah\/[^/?#]+(?:\?|.*[?&])review=1(?:$|&)/.test(parsed.path);
  } catch {
    return false;
  }
}

/** Save current page state THEN navigate to /login. Preferred over raw nav("/login"). */
export function promptLogin(navigate) {
  saveCurrentAsReturnTo();
  navigate("/login");
}

/** Save current page state THEN navigate to /register. Preferred over raw nav("/register"). */
export function promptRegister(navigate) {
  saveCurrentAsReturnTo();
  navigate("/register");
}
